// Route-table path PATTERNS (react-router `useMatch`), most-specific first.
export const SCOPED_ROUTE_PATTERN_LEAGUE =
  "/platform/:platformId/entity/:organizationId/league/:leagueId/*";
export const SCOPED_ROUTE_PATTERN_ORG = "/platform/:platformId/entity/:organizationId/*";
export const SCOPED_ROUTE_PATTERN_PLATFORM = "/platform/:platformId/*";

// Route-table PREFIXES (no trailing `/*`), most-specific first — used by `withScopedPrefix`.
export const SCOPED_ROUTE_PREFIX_LEAGUE =
  "platform/:platformId/entity/:organizationId/league/:leagueId";
export const SCOPED_ROUTE_PREFIX_ORG = "platform/:platformId/entity/:organizationId";
export const SCOPED_ROUTE_PREFIX_PLATFORM = "platform/:platformId";

export type ScopedRouteParams = {
  platformId: string | null;
  organizationId: string | null;
  leagueId: string | null;
  isScoped: boolean;
  restPath: string;
};

export const parseScopedRouteId = (value: string | undefined): string | null => {
  if (!value) return null;
  // A stringified `undefined`/`null` reaches us when a link is built from an
  // unresolved id (`entity/${org?.id}`). Treat these sentinels as missing so
  // they never override the stored/fallback scope or hit `/available/undefined`.
  if (value === "undefined" || value === "null") return null;
  return value;
};

/**
 * Build the canonical scoped path, dropping absent segments:
 *   org === null                        -> /platform/:p/<rest>
 *   org !== null, league === null       -> /platform/:p/entity/:o/<rest>
 *   org !== null, league !== null       -> /platform/:p/entity/:o/league/:l/<rest>
 *
 * A league can never exist without an organization, so `leagueId` is treated as
 * absent (defensively) whenever `organizationId` is null.
 */
export const buildScopedPath = (
  platformId: string,
  organizationId: string | null,
  leagueId: string | null,
  restPath = ""
): string => {
  const normalized = restPath.replace(/^\/+/, "");
  const suffix = normalized ? `/${normalized}` : "";

  // A league can never exist without an organization — drop it defensively.
  if (organizationId === null) {
    return `/platform/${platformId}${suffix}`;
  }

  if (leagueId === null) {
    return `/platform/${platformId}/entity/${organizationId}${suffix}`;
  }

  return `/platform/${platformId}/entity/${organizationId}/league/${leagueId}${suffix}`;
};

export const isExternalOrAnchorHref = (href: string): boolean =>
  /^(https?:|mailto:|tel:|#)/.test(href);

// True if a path already carries the platform scope prefix (any depth).
export const isAlreadyScopedPath = (path: string): boolean => /^\/platform\/[^/]+(\/|$)/.test(path);
